import { Component } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { DatePipe } from '@angular/common';
import { TankTransferComponent } from './tank-transfer/tank-transfer.component';
import { VesselTank } from './vessel-tank.model';
import { TankDebunkerComponent } from './tank-debunker/tank-debunker.component';
import { ReorderAction } from './reorder-buttons/reorder-buttons.component';
import { TankCorrectionComponent } from './tank-correction/tank-correction.component';
import { TankConsumptionComponent } from './tank-consumption/tank-consumption.component';
import { TankBunkerComponent } from './tank-bunker/tank-bunker.component';
import { PositionReportComponent } from './position-report/position-report.component';
import { ReportOperationComponent } from './report-operation/report-operation.component';
import { SpeedAndDistanceOpComponent } from './speed-and-distance-op/speed-and-distance-op.component';

export enum ReportAction {
  Consumption = 'Consumption',
  Transfer = 'Transfer',
  Bunker = 'Bunker',
  DeBunker = 'DeBunker',
  Correction = 'Correction',
  PositionReport = 'PositionReport',
  SpeedAndDistance = 'SpeedAndDistance',
}

function moveArrayItem<T>(arr: T[], fromIndex: number, toIndex: number) {
  const newArr = [...arr];

  if (
    fromIndex < 0 ||
    fromIndex >= newArr.length ||
    toIndex < 0 ||
    toIndex >= newArr.length
  ) {
    return newArr;
  }

  const item = newArr.splice(fromIndex, 1)[0];

  newArr.splice(toIndex, 0, item);

  return newArr;
}

@Component({
  selector: 're-poc-page',
  templateUrl: './poc-page.component.html',
  styleUrl: './poc-page.component.css',
  imports: [
    TankTransferComponent,
    TankDebunkerComponent,
    TankCorrectionComponent,
    TankConsumptionComponent,
    TankBunkerComponent,
    PositionReportComponent,
    ReportOperationComponent,
    SpeedAndDistanceOpComponent,
    DatePipe,
  ],
})
export class PocPageComponent {
  actions: any = [];
  reportAction: typeof ReportAction = ReportAction;
  tanks: VesselTank[] = [
    {
      id: 'tank1',
      name: 'HFO (L)',
      number: 1,
      capacity: 100,
      content: {
        fuelGrade: 'HFO',
        quantity: 80,
        bdnRef: 'BDN20250403HFO_1',
      },
    },
    {
      id: 'tank2',
      name: 'HFO (S)',
      number: 2,
      capacity: 80,
      content: {
        fuelGrade: 'HFO',
        quantity: 20,
        bdnRef: 'BDN20250403HFO_1',
      },
    },
    {
      id: 'tank3',
      name: 'LFO (L)',
      number: 3,
      capacity: 40,
    },
  ];
  reports: any = [];
  protected readonly ReportAction = ReportAction;

  selectedReportId: string[] = [];

  get sortedActions() {
    if (this.selectedReportId.length > 0) {
      return this.actions.filter(
        (a: any) => this.selectedReportId.indexOf(a.reportId) >= 0,
      );
    }

    return this.actions.filter((a: any) => !a.reportId);
  }

  isReportSelected(reportId: string) {
    return this.selectedReportId.indexOf(reportId) >= 0;
  }

  get updatedTanks(): any {
    return this.actions.reduce((acc: any, t: any) => {
      if (!t.tankOperations) return acc;

      return acc.map((tank: any) => {
        const selected = t.tankOperations.find(
          (a: any) => a.tank.id === tank.id,
        );

        if (!selected) return tank;

        return {
          ...tank,
          content: {
            quantity: selected.fuel.quantity + (tank.content?.quantity || 0),
            fuelGrade: tank.content?.fuelGrade || selected.fuel?.fuelGrade,
            bdnRef: tank.content?.bdnRef,
          },
        };
      });
    }, this.tanks);
  }

  onDelete(action: any) {
    const actionsFilt = this.actions.filter((a: any) => a.id !== action.id);
    const reportActions = actionsFilt.filter(
      (a: any) => a.reportId === action.reportId,
    );

    if (reportActions.length === 0) {
      this.reports = this.reports.filter((r: any) => r.id !== action.reportId);
    }

    this.actions = actionsFilt;
  }

  onDeleteAll() {
    this.actions = [];
    this.reports = [];
  }

  onReorder(data: { id: number; action: ReorderAction }) {
    if (this.actions.length < 2) return;

    const index = this.actions.findIndex((a: any) => a.id === data.id);
    if (index === 0 && data.action === ReorderAction.Up) return;
    if (index === this.actions.length - 1 && data.action === ReorderAction.Down)
      return;

    if (data.action === ReorderAction.Up) {
      this.actions = moveArrayItem(this.actions, index, index - 1);
    }

    if (data.action === ReorderAction.Down) {
      this.actions = moveArrayItem(this.actions, index, index + 1);
    }
  }

  getReportActions(reportId: string) {
    const arr = this.actions.filter((a: any) => a.reportId === reportId);

    return arr.length;
  }

  clickReportRow(reportId: string) {
    if (this.selectedReportId.indexOf(reportId) >= 0) {
      this.selectedReportId = this.selectedReportId.filter(
        (r: string) => r !== reportId,
      );
    } else {
      this.selectedReportId = [...this.selectedReportId, reportId];
    }
  }

  clickGenerateReport() {
    const availableActions = this.actions.filter((a: any) => !a.reportId);

    if (availableActions.length === 0) {
      alert(
        'No available actions found, please create some actions before attempting to generate a new report.',
      );

      return;
    }

    const newReport = {
      id: uuid(),
      created: new Date(),
    };

    const actionIds = availableActions.map((a: any) => a.id);
    this.actions = this.actions.map((a: any) => {
      return {
        ...a,
        reportId: actionIds.indexOf(a.id) !== -1 ? newReport.id : a.reportId,
      };
    });

    this.reports = [...this.reports, newReport];
  }

  addAction(action: ReportAction) {
    this.selectedReportId = [];

    switch (action) {
      case ReportAction.PositionReport:
        this.actions = [
          ...this.actions,
          {
            id: new Date().getTime(),
            timestamp: new Date().getTime(),
            type: ReportAction.PositionReport,
            tanks: [],
          },
        ];
        break;
      case ReportAction.SpeedAndDistance:
        this.actions = [
          ...this.actions,
          {
            id: new Date().getTime(),
            timestamp: new Date().getTime(),
            type: ReportAction.SpeedAndDistance,
            tanks: [],
          },
        ];
        break;
      case ReportAction.Consumption:
        this.actions = [
          ...this.actions,
          {
            id: new Date().getTime(),
            timestamp: new Date().getTime(),
            type: ReportAction.Consumption,
            tanks: [this.tanks[0]],
          },
        ];
        break;
      case ReportAction.Transfer:
        this.actions = [
          ...this.actions,
          {
            id: new Date().getTime(),
            timestamp: new Date().getTime(),
            type: ReportAction.Transfer,
            tanks: [this.tanks[0], this.tanks[1]],
          },
        ];
        break;
      case ReportAction.Bunker:
        this.actions = [
          ...this.actions,
          {
            id: new Date().getTime(),
            timestamp: new Date().getTime(),
            type: ReportAction.Bunker,
            tanks: [this.tanks[0], this.tanks[1]],
          },
        ];
        break;
      case ReportAction.DeBunker:
        this.actions = [
          ...this.actions,
          {
            id: new Date().getTime(),
            timestamp: new Date().getTime(),
            type: ReportAction.DeBunker,
            tanks: [this.tanks[0]],
          },
        ];
        break;
      case ReportAction.Correction:
        this.actions = [
          ...this.actions,
          {
            id: new Date().getTime(),
            timestamp: new Date().getTime(),
            type: ReportAction.Correction,
            tanks: [this.tanks[0]],
          },
        ];
        break;
      default:
        break;
    }

    setTimeout(() => {
      const el = document.querySelector('.list');

      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 10);
  }
}
